import { captureMessage } from "@sentry/nextjs";
import axios from "axios";
import { TFunction } from "i18next";
import { DateTime } from "luxon";

import { TIMEZONE, UNKNOWN_ERROR } from "../constants/constants";
import { getTimezone } from "./dateTimeFormatters";
import {
  DirectionEnum,
  DirItems,
  Hand,
  HandEnum,
  Locale,
  LOCALES_SHORT,
} from "./types";
import { formatPointNumber } from "./wv/reports/monthly/formatPointNumber";

const INVALID_DATE = "Invalid Date";
const INVALID_TIME = "Invalid Time";

// 프랑스어, 스페인어인 경우 콤마를 닷으로 변경
const commonLocales = [
  LOCALES_SHORT.FR,
  LOCALES_SHORT.ES,
  LOCALES_SHORT.IT,
  LOCALES_SHORT.DE,
  LOCALES_SHORT.SV,
];
export function isCommonLocale(locale: Locale): boolean {
  return commonLocales.includes(locale);
}

export function qs<T extends Element>(
  selector: string,
  scope: HTMLElement | Document = document
): T | null {
  if (selector === "") {
    return null;
  }
  return scope.querySelector(selector);
}

export const delay = (ms: number) => new Promise(_ => setTimeout(_, ms));

export function objectIsAllFalse(obj?: Record<string, boolean>) {
  if (!obj) {
    return false;
  }

  return Object.values(obj).some(value => value === false);
}

export const openInNewTab = (url: string) => {
  const newWindow = window.open(url, "_blank", "noopener,noreferrer");
  if (newWindow) newWindow.opener = null;
};

export const goBack = () => {
  window.history.back();
};

export const isValidNumber = (value: number): boolean => {
  return !Number.isNaN(value) && Number.isFinite(value);
};

export const isMinLengthWithoutWhitespace = (
  value: string,
  minLength: number
): boolean => {
  return value.length >= minLength && !/\s/.test(value);
};

export function clampPercentage(value: number): number | null {
  if (Number.isNaN(value)) {
    return null;
  }
  return Math.min(Math.max(value, 0), 0.99);
}

export function checkIsCancel(error: unknown): boolean {
  return axios.isCancel(error);
}

export function checkIsAbort(error: unknown): boolean {
  return (error as Error)?.name === "AbortError";
}

export function generateErrorStringOfObjectWithPrefixAndErrorName(
  prefix: string,
  errorName: string = UNKNOWN_ERROR
) {
  return {
    error: `${prefix}: ${errorName}`,
  };
}

export function IsError(error: unknown): error is Error {
  return error instanceof Error;
}

export function generateErrorString(error: unknown = UNKNOWN_ERROR) {
  return {
    error: `Error: type: ${(error as Error).name || UNKNOWN_ERROR}, message: ${
      (error as Error).message || UNKNOWN_ERROR
    }`,
  };
}

export function handleErrors(prefix: string, error: unknown) {
  if (IsError(error)) {
    if (checkIsCancel(error)) {
      return generateErrorStringOfObjectWithPrefixAndErrorName(
        prefix,
        "CanceledError"
      );
    }
    if (checkIsAbort(error)) {
      return generateErrorStringOfObjectWithPrefixAndErrorName(
        prefix,
        "AbortError"
      );
    }

    return generateErrorString(error);
  }

  return generateErrorString();
}

export const safelyHandlePlayPromise = (
  playPromise: Promise<void> | undefined
) => {
  if (playPromise !== undefined) {
    playPromise.then(() => undefined).catch(() => undefined);
  }
};

export function isStorageAvailable(storage: Storage | undefined): boolean {
  // Check if storage is available.
  if (!storage) {
    return false;
  }

  // Check if the storage can actually be accessed.
  try {
    const now = Date.now();
    const testItemKey = `storage-test-entry-${now}`;
    const testItemValue = `storage-test-value-${now}`;
    storage.setItem(testItemKey, testItemValue);
    const retrievedItemValue = storage.getItem(testItemKey);
    storage.removeItem(testItemKey);

    return retrievedItemValue === testItemValue;
  } catch {
    return false;
  }
}

export const kebabCase = (string: string) =>
  string
    .replace(/([a-z])([A-Z])/g, "$1-$2")
    .replace(/[\s_]+/g, "-")
    .toLowerCase();

//! : 다국어 지원을 위해 정규표현식 수정 필요
export const sanitizeID = (string: string) => {
  const kebabString = kebabCase(string);
  return kebabString.replace(/[^a-zA-Z0-9-_가-힣一-龯ぁ-ゔァ-ヴー々〆〤]/g, "");
};

export function displayValueOrDash(value?: number | null) {
  return value ?? "-";
}

export type StatusOfCreateI18nKey = "increase" | "decrease" | "same";

export function createI18nKey(scoreDifference: number): StatusOfCreateI18nKey {
  let i18nKey: StatusOfCreateI18nKey = "same"; // 기본값: 점수 동일

  if (scoreDifference > 0) {
    i18nKey = "increase"; // 점수 증가
  } else if (scoreDifference < 0) {
    i18nKey = "decrease"; // 점수 감소
  }

  return i18nKey;
}

/**
 * 숫자를 지정된 정밀도로 포맷팅하는 함수
 * @param score - 포맷팅할 숫자
 * @param locale - 포맷팅에 사용할 로케일 (예: 'en', 'fr', 'ko')
 * @param minimumFractionDigits - 소수점 이하 자릿수의 최소값을 지정하는 선택적 매개변수
 * @param precision - 소수점 이하 자릿수 (기본값: 1)
 * @returns 포맷팅된 숫자 문자열
 */
export function formatToPrecision(
  score: number,
  locale: Locale,
  minimumFractionDigits?: number,
  precision = 1
): string {
  // score를 Number로 감싸서 숫자로 변환하고 NaN인 경우는 0으로 반환
  const scoreNumber = Number(score);
  const isCommonLocaleResult = isCommonLocale(locale);
  const defaultZero = isCommonLocaleResult ? "0,0" : "0.0";

  // 유효하지 않거나 0인 값에 대한 조기 반환
  if (Number.isNaN(scoreNumber) || scoreNumber === 0) {
    return defaultZero;
  }

  // 지정된 정밀도로 반올림
  const roundedNumber = scoreNumber.toFixed(precision);

  // 라틴 언어인 경우 포맷팅
  if (isCommonLocaleResult) {
    return formatPointNumber(
      parseFloat(roundedNumber),
      locale,
      minimumFractionDigits
    );
  }

  // 소수점 이하 자릿수의 최소값을 지정하는 선택적 매개변수가 있는 경우
  //  80.0 ? 80.0 : 80
  return minimumFractionDigits
    ? roundedNumber
    : parseFloat(roundedNumber).toString();
}

export function splitDecimal(score: number) {
  const roundedScore = Math.round(score * 10) / 10; // 소수점 첫째 자리까지 반올림
  const integerPart = Math.floor(roundedScore); // 소수점 위의 정수 부분
  const decimalPart = Math.round((roundedScore - integerPart) * 10); // 소수점 아래 첫째 자리

  return { n: integerPart, m: decimalPart };
}

export const createLabels = (length: number) => {
  const l = [];
  for (let i = 1; i <= length; i += 1) {
    l.push(`${i}`);
  }
  return l;
};

// 2023-11-01T08:00:00Z -> 2023-06-11, 17:14:55 형태로 변환 (SD-8379 기본 출력 ymd/hms)
export function formatTimestamp(timestamp: string | null) {
  if (!timestamp) {
    return { formattedDate: INVALID_DATE, formattedTime: INVALID_TIME };
  }

  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime())) {
    // 유효하지 않은 타임스탬프에 대해 Sentry로 로그 기록
    captureMessage("Invalid timestamp: Cannot format to local time zone.", {
      level: "warning",
      tags: {
        timestamp, // 문제가 발생한 타임스탬프
        function: "formatTimestamp", // 문제가 발생한 함수
      },
    });

    return { formattedDate: INVALID_DATE, formattedTime: INVALID_TIME };
  }

  // 날짜와 시간을 개별적으로 얻기 위해 get 메서드를 사용합니다.
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0"); // getMonth()는 0부터 시작합니다.
  const day = date.getDate().toString().padStart(2, "0");
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const seconds = date.getSeconds().toString().padStart(2, "0");

  // 날짜와 시간을 'YYYY-MM-DD'와 'HH:MM:SS' 형식으로 변환합니다.
  const formattedDate = `${year}-${month}-${day}`;
  const formattedTime = `${hours}:${minutes}:${seconds}`;

  return { formattedDate, formattedTime };
}

/*
// UTC 기준 타임스탬프
2012-02-11T08:32:45+00:00"
-> 사용자의 로컬 타임존을 반영한 2023-06-11, 17:14:55 형태로 변환 (SD-8379 기본 출력 ymd/hms)
*/
export function formatTimestampToLocalLuxon(timestamp: string) {
  // 사용자의 현재 타임존을 얻음 (SSR은 사용자 위치를 모르므로 UTC — formatBasicDateTime과 동일 기준)
  const userTimeZone =
    typeof window !== "undefined" ? getTimezone() : TIMEZONE.UTC;

  // 입력된 타임스탬프를 UTC로 파싱하고, 사용자의 타임존으로 변환
  const dateTime = DateTime.fromISO(timestamp, { zone: "utc" }).setZone(
    userTimeZone
  );

  if (!dateTime.isValid) {
    // 유효하지 않은 타임스탬프에 대해 Sentry로 로그 기록
    captureMessage("Invalid timestamp: Cannot format to local time zone.", {
      level: "warning",
      tags: {
        timestamp, // 문제가 발생한 타임스탬프
        function: "formatTimestampToLocalLuxon", // 문제가 발생한 함수
      },
    });
    return { date: INVALID_DATE, time: INVALID_TIME };
  }

  // 포맷에 맞게 날짜와 시간을 변환
  const formattedDate = dateTime.toFormat("yyyy-MM-dd");
  const formattedTime = dateTime.toFormat("HH:mm:ss");

  // 날짜와 시간을 객체 형식으로 반환
  return {
    date: formattedDate,
    time: formattedTime,
  };
}

// n번째를 영문으로 나타내기 위한 함수
export function getOrdinalIndicatorEn(number: number) {
  const j = number % 10;
  const k = number % 100;
  if (j === 1 && k !== 11) {
    return `${number}st`;
  }
  if (j === 2 && k !== 12) {
    return `${number}nd`;
  }
  if (j === 3 && k !== 13) {
    return `${number}rd`;
  }
  return `${number}th`;
}

// n번째를 지역화된 언어로 나타내기 위한 함수
export function getLocalizedOrdinal(number: number, locale: Locale) {
  switch (locale) {
    case LOCALES_SHORT.KO:
      return `${number}번째`;
    case LOCALES_SHORT.EN:
      return getOrdinalIndicatorEn(number);
    case LOCALES_SHORT.FR:
      if (number === 1) {
        return "1er";
      }
      return `${number}e`;
    case LOCALES_SHORT.JA:
      return `${number}番目`;
    case LOCALES_SHORT.ES:
      return `${number}º`;
    case LOCALES_SHORT["ZH-CN"]:
    case LOCALES_SHORT["ZH-TW"]:
      return `第${number}`;
    case LOCALES_SHORT.DE:
      return `${number}.`;
    case LOCALES_SHORT.IT:
      return `${number}º`;
    case LOCALES_SHORT.SV:
      if (number === 1 || number === 2) {
        return ":a";
      }
      return `${number}:e`;
    default:
      return number.toString();
  }
}

const japaneseChineseLocales = [
  LOCALES_SHORT.JA,
  // 25.09.23 신규
  LOCALES_SHORT["ZH-CN"],
  LOCALES_SHORT["ZH-TW"],
];
export function isJapaneseChineseLocale(locale: Locale): boolean {
  return japaneseChineseLocales.includes(locale);
}

const asianLocales = [
  LOCALES_SHORT.KO,
  LOCALES_SHORT.JA,
  // 25.09.23 신규
  LOCALES_SHORT["ZH-CN"],
  LOCALES_SHORT["ZH-TW"],
];
export function isAsianLocale(locale: Locale): boolean {
  return asianLocales.includes(locale);
}

/**
 * 문자열의 첫 글자를 대문자로 변환하는 함수
 * @param word 변환할 문자열
 * @returns 첫 글자가 대문자로 변환된 문자열
 * @example
 * capitalize('hello') // 'Hello'
 * capitalize('world') // 'World'
 * capitalize('') // ''
 */
function capitalize(word: string) {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

export function createDirItems(
  dirT: TFunction,
  hand: Hand = HandEnum.RIGHT
): DirItems {
  // 우타, 좌타에 따른 Direction 처리
  if (hand === HandEnum.LEFT) {
    return {
      // ! : problemHeader는 첫글자만 대문자
      title_dir_arm_2: capitalize(dirT(`dir_arm_1`)),
      title_dir_knee_2: capitalize(dirT(`dir_knee_1`)),
      dir_1: dirT(`dir_2`),
      dir_2: dirT(`dir_1`),
      dir_arm_1: dirT(`dir_arm_2`),
      dir_arm_2: dirT(`dir_arm_1`),
      dir_elbow_1: dirT(`dir_elbow_2`),
      dir_elbow_2: dirT(`dir_elbow_1`),
      dir_hip_1: dirT(`dir_hip_2`),
      dir_hip_2: dirT(`dir_hip_1`),
      dir_knee_1: dirT(`dir_knee_2`),
      dir_knee_2: dirT(`dir_knee_1`),
      dir_wrist_1: dirT(`dir_wrist_2`),
      dir_wrist_2: dirT(`dir_wrist_1`),
      dir_shoulder_1: dirT(`dir_shoulder_2`),
      dir_shoulder_2: dirT(`dir_shoulder_1`),
      dir_l_body_1: dirT(`dir_l_body_2`),
      dir_l_body_2: dirT(`dir_l_body_1`),
      dir_b_hand_1: dirT(`dir_b_hand_2`),
      dir_b_hand_2: dirT(`dir_b_hand_1`),
      dir_hand_1: dirT(`dir_hand_2`),
      dir_hand_2: dirT(`dir_hand_1`),
      dir_leg_1: dirT(`dir_leg_2`),
      dir_leg_2: dirT(`dir_leg_1`),
      dir_foot_1: dirT(`dir_foot_2`),
      dir_foot_2: dirT(`dir_foot_1`),
      dir_swing_1: dirT(`dir_swing_2`),
      dir_swing_2: dirT(`dir_swing_1`),
      dir_toe_1: dirT(`dir_toe_2`),
      dir_toe_2: dirT(`dir_toe_1`),
      dir_heel_1: dirT(`dir_heel_2`),
      dir_heel_2: dirT(`dir_heel_1`),
      dir_armpit_1: dirT(`dir_armpit_2`),
      dir_armpit_2: dirT(`dir_armpit_1`),
      dir_thigh_1: dirT(`dir_thigh_2`),
      dir_thigh_2: dirT(`dir_thigh_1`),
      dir_palm_1: dirT(`dir_palm_2`),
      dir_palm_2: dirT(`dir_palm_1`),
      dir_big_toe_1: dirT(`dir_big_toe_2`),
      dir_big_toe_2: dirT(`dir_big_toe_1`),
      dir_pelvis_1: dirT(`dir_pelvis_2`),
      dir_pelvis_2: dirT(`dir_pelvis_1`),
    };
  }

  // RIGHT(우타일 경우 - 기본값)
  return {
    // ! : problemHeader는 첫글자만 대문자
    title_dir_arm_2: capitalize(dirT(`dir_arm_2`)),
    title_dir_knee_2: capitalize(dirT(`dir_knee_2`)),
    dir_1: dirT(`dir_1`),
    dir_2: dirT(`dir_2`),
    dir_arm_1: dirT(`dir_arm_1`),
    dir_arm_2: dirT(`dir_arm_2`),
    dir_elbow_1: dirT(`dir_elbow_1`),
    dir_elbow_2: dirT(`dir_elbow_2`),
    dir_hip_1: dirT(`dir_hip_1`),
    dir_hip_2: dirT(`dir_hip_2`),
    dir_knee_1: dirT(`dir_knee_1`),
    dir_knee_2: dirT(`dir_knee_2`),
    dir_wrist_1: dirT(`dir_wrist_1`),
    dir_wrist_2: dirT(`dir_wrist_2`),
    dir_shoulder_1: dirT(`dir_shoulder_1`),
    dir_shoulder_2: dirT(`dir_shoulder_2`),
    dir_l_body_1: dirT(`dir_l_body_1`),
    dir_l_body_2: dirT(`dir_l_body_2`),
    dir_b_hand_1: dirT(`dir_b_hand_1`),
    dir_b_hand_2: dirT(`dir_b_hand_2`),
    dir_hand_1: dirT(`dir_hand_1`),
    dir_hand_2: dirT(`dir_hand_2`),
    dir_leg_1: dirT(`dir_leg_1`),
    dir_leg_2: dirT(`dir_leg_2`),
    dir_foot_1: dirT(`dir_foot_1`),
    dir_foot_2: dirT(`dir_foot_2`),
    dir_swing_1: dirT(`dir_swing_1`),
    dir_swing_2: dirT(`dir_swing_2`),
    dir_toe_1: dirT(`dir_toe_1`),
    dir_toe_2: dirT(`dir_toe_2`),
    dir_heel_1: dirT(`dir_heel_1`),
    dir_heel_2: dirT(`dir_heel_2`),
    dir_armpit_1: dirT(`dir_armpit_1`),
    dir_armpit_2: dirT(`dir_armpit_2`),
    dir_thigh_1: dirT(`dir_thigh_1`),
    dir_thigh_2: dirT(`dir_thigh_2`),
    dir_palm_1: dirT(`dir_palm_1`),
    dir_palm_2: dirT(`dir_palm_2`),
    dir_big_toe_1: dirT(`dir_big_toe_1`),
    dir_big_toe_2: dirT(`dir_big_toe_2`),
    dir_pelvis_1: dirT(`dir_pelvis_1`),
    dir_pelvis_2: dirT(`dir_pelvis_2`),
  };
}

export function getProblemImagePath(
  problemId: number,
  hand: Hand = HandEnum.RIGHT
): string {
  if (
    // 이미지 없음
    problemId === 77 ||
    problemId === 83 ||
    problemId === 84 ||
    problemId === 85 ||
    problemId === 86 ||
    problemId === 87 ||
    problemId === 88 ||
    problemId === 89 ||
    problemId === 90
  ) {
    return "";
  }

  let direction = DirectionEnum.FRONT;

  if (
    // 정면 문제점 이미지가 없는 경우, 측면 문제점 이미지 사용
    problemId === 0 ||
    problemId === 50 ||
    problemId === 55 ||
    problemId === 56 ||
    problemId === 57 ||
    problemId === 58 ||
    problemId === 59 ||
    problemId === 60 ||
    problemId === 62 ||
    problemId === 63 ||
    problemId === 65 ||
    problemId === 66 ||
    problemId === 67 ||
    problemId === 68 ||
    problemId === 69 ||
    problemId === 73 ||
    problemId === 79 ||
    problemId === 81
    // 이후 부터는 이미지 없음
    // problemId === 86 ||
    // problemId === 87 ||
    // problemId === 88 ||
    // problemId === 89 ||
    // problemId === 90
  ) {
    direction = DirectionEnum.SIDE;
  }

  return `/images/ai-report/sp-img/${hand}/${direction}/${problemId}.png`;
}
